import { Fragment, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronRight, ChevronUp, Clock3, LayoutDashboard, CheckSquare, User } from "lucide-react";
import { LauncherIcon } from "./icons/AppIcons";
import { timeAgo } from "../lib/apps";
import { matchesPath } from "../lib/navigation";
import { MobileSheet, MobileSheetBody, MobileSheetContent, MobileSheetTitle } from "./ui/mobile-sheet";

// Phone and tablet tab bar (Layout hides <aside> below lg): Accueil · Tâches · Applications (au centre) · Récents · Profil. Everything comes
// already permission-filtered from Layout.jsx, so phone and computer can't drift apart. « Applications » opens the centre; inside
// an application it becomes that application's menu (its entries, and a way back to the centre).
const tabClass = (active) => `flex-1 flex flex-col items-center justify-center gap-0.5 text-2xs font-medium ${active ? "text-white" : "text-white/60"}`;

export default function MobileBottomNav({ globalItems, currentApp, recents }) {
  const { pathname } = useLocation();
  const [appSheet, setAppSheet] = useState(false);
  const [recentSheet, setRecentSheet] = useState(false);
  const tasks = globalItems.find((item) => item.id === "tasks");
  const inApps = pathname === "/apps" || !!currentApp;
  const AppsIcon = currentApp ? currentApp.icon : LauncherIcon;

  const appsTab = (
    <>
      <span className="relative">
        <AppsIcon size={20} />
        {currentApp?.badge > 0 && <span className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" data-testid="bottomnav-apps-dot" />}
      </span>
      <span className="flex max-w-full items-center justify-center gap-0.5 px-1">
        <span className="truncate">{currentApp ? currentApp.label : "Applications"}</span>
        {currentApp && <ChevronUp size={12} className="shrink-0" aria-hidden="true" />}
      </span>
    </>
  );

  return (
    <>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-jamora [--jamora-icon-navy:#ffffff] border-t border-white/10 flex items-stretch h-16 pb-[env(safe-area-inset-bottom,0px)]"
        data-testid="mobile-bottom-nav" aria-label="Navigation principale">
        <NavLink to="/dashboard" data-testid="bottomnav-nav-dashboard" className={({ isActive }) => tabClass(isActive)}>
          <LayoutDashboard size={20} />
          Accueil
        </NavLink>
        {tasks && (
          <NavLink to={tasks.to} data-testid="bottomnav-nav-tasks" className={({ isActive }) => tabClass(isActive)}>
            <CheckSquare size={20} />
            Tâches
          </NavLink>
        )}
        {currentApp ? (
          <button type="button" onClick={() => setAppSheet(true)} data-testid="bottomnav-apps" aria-haspopup="true" aria-expanded={appSheet}
            aria-current="page" className={tabClass(true)}>{appsTab}</button>
        ) : (
          <NavLink to="/apps" data-testid="bottomnav-apps" className={() => tabClass(inApps)}>{appsTab}</NavLink>
        )}
        <button type="button" onClick={() => setRecentSheet(true)} data-testid="bottomnav-recents" aria-haspopup="true" aria-expanded={recentSheet} className={tabClass(false)}>
          <Clock3 size={20} />
          Récents
        </button>
        <NavLink to="/profile" data-testid="bottomnav-profile" className={({ isActive }) => tabClass(isActive)}>
          <User size={20} />
          Profil
        </NavLink>
      </nav>

      {/* The current application's own menu. */}
      {currentApp && (
        <MobileSheet open={appSheet} onOpenChange={setAppSheet}>
          <MobileSheetContent closeOnHandle className="lg:hidden" aria-describedby={undefined}>
            <MobileSheetTitle className="sr-only">{currentApp.label}</MobileSheetTitle>
            <MobileSheetBody>
              <nav className="space-y-0.5 pt-2" data-testid="mobile-app-sheet">
                <div className="flex items-center gap-2 px-2 pb-1 text-sm font-semibold text-slate-900">
                  <currentApp.icon size={18} className="text-slate-500" aria-hidden="true" />
                  {currentApp.label}
                </div>
                <div className="ml-4 border-l border-slate-200 pl-2 space-y-0.5">
                  {currentApp.items.map((n, i) => (
                    <Fragment key={n.id}>
                      {n.group && currentApp.items[i - 1]?.group !== n.group && (
                        <p className="px-2 pt-3 pb-1 text-2xs font-semibold uppercase tracking-wider text-slate-400" data-testid="bottomnav-group">{n.group}</p>
                      )}
                      <SheetEntry n={n} pathname={pathname} onOpen={() => setAppSheet(false)} />
                    </Fragment>
                  ))}
                </div>
                <div className="mt-2 border-t border-slate-100 pt-2">
                  <NavLink to="/apps" onClick={() => setAppSheet(false)} data-testid="bottomnav-all-apps"
                    className="flex items-center gap-3 px-2 py-3 rounded-lg text-sm text-slate-700 hover:bg-slate-50">
                    <LauncherIcon size={18} className="text-slate-500 shrink-0" />
                    <span className="flex-1">Toutes les applications</span>
                    <ChevronRight size={16} className="text-slate-300 shrink-0" />
                  </NavLink>
                </div>
              </nav>
            </MobileSheetBody>
          </MobileSheetContent>
        </MobileSheet>
      )}

      <MobileSheet open={recentSheet} onOpenChange={setRecentSheet}>
        <MobileSheetContent closeOnHandle className="lg:hidden" aria-describedby={undefined}>
          <MobileSheetTitle className="sr-only">Récents</MobileSheetTitle>
          <MobileSheetBody>
            <nav className="space-y-0.5 pt-2" data-testid="mobile-recents-sheet">
              <div className="px-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-slate-400">Dernières pages consultées</div>
              {recents.length === 0 ? (
                <p className="px-2 py-4 text-sm text-slate-500" data-testid="mobile-recents-empty">Les pages que vous ouvrez apparaîtront ici.</p>
              ) : recents.map((r) => (
                <NavLink key={r.id} to={r.item.to} onClick={() => setRecentSheet(false)} data-testid={`mobile-recent-${r.id}`}
                  className="flex items-center gap-3 px-2 py-3 rounded-lg text-sm text-slate-700 hover:bg-slate-50">
                  <r.item.icon size={18} className="text-slate-500 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{r.item.label}</span>
                    {r.appLabel && <span className="block truncate text-xs text-slate-400">{r.appLabel}</span>}
                  </span>
                  <span className="text-xs text-slate-400">{timeAgo(r.at)}</span>
                </NavLink>
              ))}
            </nav>
          </MobileSheetBody>
        </MobileSheetContent>
      </MobileSheet>
    </>
  );
}

// One entry of an application's sheet. A planned module is shown greyed with « Bientôt » (a phone has no hover for a tooltip).
function SheetEntry({ n, pathname, onOpen }) {
  if (n.upcoming) {
    return (
      <div aria-disabled="true" data-testid={`bottomnav-more-${n.testid}`} className="flex items-center gap-3 px-2 py-3 rounded-lg text-sm text-slate-400">
        <n.icon size={18} className="text-slate-300 shrink-0" />
        <span className="flex-1">{n.label}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-2xs font-medium text-slate-500">Bientôt</span>
      </div>
    );
  }
  return (
    <NavLink
      to={n.to}
      data-testid={`bottomnav-more-${n.testid}`}
      onClick={onOpen}
      aria-current={matchesPath(n, pathname) ? "page" : "false"}
      className={`flex items-center gap-3 px-2 py-3 rounded-lg text-sm hover:bg-slate-50 ${matchesPath(n, pathname) ? "bg-slate-100 font-medium text-slate-900" : "text-slate-700"}`}
    >
      <n.icon size={18} className="text-slate-500 shrink-0" />
      <span className="flex-1">{n.label}</span>
      {n.badge > 0 && (
        <span className="rounded-full bg-amber-100 px-1.5 text-2xs font-semibold text-amber-800" aria-label={`${n.badge} ${n.badgeLabel || "en attente"}`} data-testid={n.badgeTestid ? `${n.badgeTestid}-mobile` : undefined}>{n.badge}</span>
      )}
      <ChevronRight size={16} className="text-slate-300 shrink-0" />
    </NavLink>
  );
}
